import React, { useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  preloadHome, 
  preloadAbout, 
  preloadEvents, 
  preloadCalendar, 
  preloadGallery, 
  preloadMembership, 
  preloadJoinNow, 
  preloadTerms, 
  preloadPrivacy, 
  preloadAdmin 
} from '../pages/LazyPages';

interface EnhancedNavigationProps {
  children: React.ReactNode;
}

const EnhancedNavigation: React.FC<EnhancedNavigationProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Preload functions mapping
  const preloadFunctions = {
    '/': preloadHome,
    '/about': preloadAbout,
    '/events': preloadEvents,
    '/calendar': preloadCalendar,
    '/gallery': preloadGallery,
    '/membership': preloadMembership,
    '/join': preloadJoinNow,
    '/terms': preloadTerms,
    '/privacy': preloadPrivacy,
    '/admin': preloadAdmin,
  };

  // Preload adjacent pages for better navigation experience
  const preloadAdjacentPages = useCallback((currentPath: string) => {
    const pathMap: { [key: string]: string[] } = {
      '/': ['/about', '/events'],
      '/about': ['/', '/events', '/membership'],
      '/events': ['/about', '/calendar', '/gallery'],
      '/calendar': ['/events', '/gallery'],
      '/gallery': ['/events', '/calendar', '/membership'],
      '/membership': ['/about', '/gallery', '/join'],
      '/join': ['/membership', '/terms'],
      '/terms': ['/join', '/privacy'],
      '/privacy': ['/terms'],
      '/admin': []
    };

    const adjacentPaths = pathMap[currentPath] || [];
    adjacentPaths.forEach(path => {
      const preloadFn = preloadFunctions[path as keyof typeof preloadFunctions];
      if (preloadFn) {
        preloadFn().catch(() => {});
      }
    });
  }, [preloadFunctions]);

  // Enhanced navigation with smooth transitions
  const enhancedNavigate = useCallback((path: string) => {
    // Add loading state to body for smooth transition
    document.body.classList.add('page-transitioning');
    
    // Preload the target page
    const preloadFn = preloadFunctions[path as keyof typeof preloadFunctions];
    if (preloadFn) {
      preloadFn().then(() => {
        // Navigate after preload is complete
        navigate(path);
        // Remove loading state after a short delay
        setTimeout(() => {
          document.body.classList.remove('page-transitioning');
        }, 100);
      }).catch(() => {
        // Navigate even if preload fails
        navigate(path);
        setTimeout(() => {
          document.body.classList.remove('page-transitioning');
        }, 100);
      });
    } else {
      navigate(path);
      setTimeout(() => {
        document.body.classList.remove('page-transitioning');
      }, 100);
    }
  }, [navigate, preloadFunctions]);

  // Preload adjacent pages when location changes
  useEffect(() => {
    preloadAdjacentPages(location.pathname);
  }, [location.pathname, preloadAdjacentPages]);

  // Add global navigation enhancement
  useEffect(() => {
    // Add CSS for page transitions
    const style = document.createElement('style');
    style.textContent = `
      .page-transitioning {
        opacity: 0.95;
        transition: opacity 0.2s ease-in-out;
      }
      
      .page-transitioning * {
        pointer-events: none;
      }
      
      /* Smooth scroll behavior for all elements */
      html {
        scroll-behavior: smooth;
      }
      
      /* Enhanced focus styles for accessibility */
      a:focus-visible,
      button:focus-visible,
      [role="button"]:focus-visible {
        outline: 2px solid #3b82f6;
        outline-offset: 2px;
        border-radius: 4px;
      }
      
      /* Reduce motion for users who prefer it */
      @media (prefers-reduced-motion: reduce) {
        html {
          scroll-behavior: auto;
        }
        
        .page-transitioning {
          transition: none;
        }
      }
    `;
    document.head.appendChild(style);

    // Expose enhanced navigation globally for use in other components
    (window as any).enhancedNavigate = enhancedNavigate;

    return () => {
      document.head.removeChild(style);
      delete (window as any).enhancedNavigate;
    };
  }, [enhancedNavigate]);

  return <>{children}</>;
};

export default EnhancedNavigation;
